import { computed, nextTick, ref, onMounted, onUnmounted } from 'vue';
import { defineStore } from 'pinia'
import axios from 'axios';
import { v4 as useId } from 'uuid';

import { FormatStyles } from '@/util/FormatStyles.js';

import { Authenticator } from '@aws-amplify/ui-vue';
import "@aws-amplify/ui-vue/styles.css";

// REST API connection
import { Amplify, Auth } from 'aws-amplify';
import awsconfig from '@/aws-exports';
import secrets from '../../secrets.json';

Amplify.configure(awsconfig);

async function getCognitoToken() {
  try {
    const session = await Auth.currentSession();
    // Fetch the ID token (or Access token depending on your API setup)
    const idToken = session.getIdToken().getJwtToken();
    return idToken;
  } catch (error) {
    console.error('Error fetching Cognito token:', error);
    return null;
  }
}


const loggedIn = ref(false);
const offline = secrets.offline;
const api = offline ? 'http://localhost:3000/dev/trivia' : secrets.restApi;

const auth = async function makeAuth(user) {
  const token = await getCognitoToken();
  if (token && user && !offline) {
    const config = {
      headers: {
        // 'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'sub': user.attributes.sub,
        // 'Content-Type': 'multipart/form-data', // need this if submitting files via formData
      },
    };
    return config;
  }
  if (offline) {
    const config = {
      headers: {
        authorization: 'b22fa780-f318-4216-9ded-865a0557b47d',
        // dbversion: state.dbversion,
        // sub: 'b22fa780-f318-4216-9ded-865a0557b47d',
      },
    };
    return config;
  }
  return null;
};

export const useWebSocketStore = defineStore('websocket', () => {
  const triviaStore = useTriviaStore();
  const enableRemote = computed(() => triviaStore.connectToRemote);
  const clientType = computed(() => triviaStore.clientType);
  const socket = ref(null);
  const connectionStatus = ref('Connecting');
  const serverMessage = ref({
    message: '',
    _id: null,
  });
  const message = ref('');
  // Initialize WebSocket connection
  const initWebSocket = async () => {
    const token = await getCognitoToken();
    if (!token) {
      console.error('No token available for WebSocket connection');
      return;
    }
    socket.value = new WebSocket(secrets.websocket, [], { headers: { 'Authorization': token } });
    // WebSocket open
    socket.value.onopen = () => {
      connectionStatus.value = 'Connected';
      triviaStore.connectToRemoteStart = new Date();
      ping();
      const keepAlive = setInterval(() => {
        let connectionShouldTimeOut = false;
        const now = new Date();
        const diff = now - triviaStore.connectToRemoteStart;
        if (diff > 4 * 60 * 60 * 1000) { // 4 hours
          connectionShouldTimeOut = true;
        }
        if (connectionShouldTimeOut) {
          console.log('WebSocket connection timed out');
          clearInterval(keepAlive);
          socket.value.close();
        } else {
          ping();
        }
      }, 15000);
      console.log('WebSocket connected');
    };

    // Handle incoming messages
    socket.value.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      let message = data.message;
      try {
        message = JSON.parse(data.message);
      } catch (e) {
        message = data.message;
      }
      try {
        message = JSON.parse(message.message);
      } catch (e) {}
      triviaStore.serverMessage._id = useId();
      triviaStore.serverMessage.message = message;
      console.log('Received:', message);
      switch (message) {
        case 'Controls Connected': {
          console.log('Controls App connected');
          triviaStore.connectToRemoteStatus = true;
          break;
        }
        case 'Controls Disconnected': {
          console.log('Controls App disconnected');
          triviaStore.connectToRemoteStatus = false;
          break;
        }
        default:
          break;
      }
      if (message.status === 'Controls Connected') {
        triviaStore.connectToRemoteStatus = true;
      }
      if (message.status === 'Presenter Connected') {
        console.log('loading game', message.gameId);
        if (message.gameId && (!triviaStore.CurrentGame._id || triviaStore.CurrentGame._id !== message.gameId)) {
          await triviaStore.loadGame(message.gameId);
        }
      }
      if (message.showInterlude !== undefined) {
        triviaStore.setInterlude(message.showInterlude, true);
      }
      if (message.activeRound !== undefined) {
        triviaStore.setRound(message.activeRound, message.activeQuestion, true);
      }
      if (message.timer !== undefined) {
        triviaStore.setTimer({ timer: message.timer }, true);
      }
      if (message.timeLeft !== undefined) {
        triviaStore.setTimer({ timeLeft: message.timeLeft }, true);
      }
      if (message.setAnswer !== undefined) {
        triviaStore.setAnswer(message.setAnswer, true);
      }
      if (message.team !== undefined) {
        triviaStore.setTeam(message.team, true);
      }
      if (message.activeQuestion !== undefined) {
        triviaStore.setQuestion(message.activeQuestion, true);
      }
      if (message.scoreboard !== undefined) {
        triviaStore.updateScoreboard(message.scoreboard, true);
      }
      if (message.showScoreboard !== undefined) {
        triviaStore.CurrentGame.showScoreboard = message.showScoreboard;
      }
      if (message.questionStep !== undefined) {
        triviaStore.advanceQuestion(true);
      }
    };

    // WebSocket close
    socket.value.onclose = () => {
      connectionStatus.value = 'Disconnected';
      console.log('WebSocket disconnected');
    };

    // WebSocket error
    socket.value.onerror = (error) => {
      console.error('WebSocket error:', error);
      connectionStatus.value = 'Error';
    };
  };

  if (!enableRemote) {
    const sendMessage = () => {
      console.log('Message not sent. WebSocket disabled.');
    };
    connectionStatus.value = 'Disconnected';
    triviaStore.serverMessage._id = useId();
    triviaStore.serverMessage.message = 'WebSocket disabled';
    return { initWebSocket, sendMessage, connectionStatus, serverMessage, message };
  }
  const ping = () => {
    const payload = {
      action: 'ping',
      message: {
        clientType: triviaStore.clientType,
        gameId: triviaStore.CurrentGame._id ? triviaStore.CurrentGame._id : null,
      },
    };
    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify(payload));
    } else {
      console.error('WebSocket is not open');
    }
  };
  // Send a message
  const sendMessage = (messageContent) => {
    const payload = {
      action: 'sendMessage', // Custom WebSocket route
      message: messageContent,
    };

    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify(payload));
      message.value = ''; // Clear after sending
    } else {
      console.error('WebSocket is not open');
    }
  };
  // Subscribe
  const subscribe = (messageContent) => {
    const payload = {
      action: 'subscribe', // Custom WebSocket route
      message: messageContent,
    };

    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify(payload));
      message.value = ''; // Clear after sending
    } else {
      console.error('WebSocket is not open');
    }
  };

  // Automatically initialize WebSocket when the store is used
  onMounted(() => {
    initWebSocket();
  });

  // Close the WebSocket when the component using the store is unmounted
  onUnmounted(() => {
    if (socket.value) {
      console.log('closing websocket');
      sendMessage('Controls Disconnected');
      const checkIfSent = () => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.value.close();
        } else {
          setTimeout(checkIfSent, 100);
        }
      };
      checkIfSent();
    }
  });

  return {
    connectionStatus,
    serverMessage,
    message,
    ping,
    initWebSocket,
    sendMessage,
    subscribe,
  };
});

function CurrentGameDefaults() {
  return {
    dateCreated: new Date(),
    dateModified: new Date(),
    activeQuestion: 0,
    activeRound: 0,
    questionStep: 0,
    questionSubStep: 0,
    showCategoryIntro: false,
    timer: false,
    timeLeft: 0,
    showAnswer: false,
    showScoreboard: false,
    scoreboard: [],
    rounds: {},
    interlude: {
      show: true,
      useInterlude: true,
      text: {
        perRound: false,
        all: '',
        rounds: [],
      },
      image: {
        show: true,
        entry: '',
        link: '',
        perRound: false,
        rounds: [],
      }
    }
  };
}

let timerIntervals = [];

export const useTriviaStore = defineStore({
  id: 'triviaStore',
  state: () => ({
    user: null,
    Profile: {},
    loggedIn: false,
    readyToRun: false,
    loading: false,
    systemErrorText: null,
    upcomingSegment: null,
    status: null,
    serverMessage: {
      message: '',
      _id: null,
    },
    preferences: {
      animateScores: true,
      showCategoryIntro: true,
      categoryIntroDuration: 3650,
    },
    connectToRemote: false,
    connectToRemoteStatus: false,
    connectToRemoteStart: null,
    clientType: null,
    copyQuestion: null,
    CurrentGame: CurrentGameDefaults(),
    CurrentGameRevert: CurrentGameDefaults(),
    LastGame: CurrentGameDefaults(),
    SavedQuestion: null,
    themes: FormatStyles,
    lastRound: 0,
    activeGame: [], // Currently used for importing old questions. Forward, use currentGame
    questions: [],
    hosts: [],
    games: [],
    venues: [],
    schema: {},
    webSocketStore: useWebSocketStore(),
  }),
  getters: {
    all: (state) => {
      return {
        questions: state.questions,
        hosts: state.hosts,
        games: state.games,
        venues: state.venues,
        schema: state.schema,
        currentGame: state.CurrentGame,
      };
    },
  },
  actions: {
    async resetGame() {
      this.CurrentGame = CurrentGameDefaults();
      console.log('game reset');
    },
    async initialLoad() {
      console.log('initial load');
      this.loading = true;
      axios.get(`${api}/all`, await auth(this.user))
      .then(async (response) => {
        console.log('initial load successful');
        for (let a = 0; a < Object.keys(response.data).length; a += 1) {
          const key = Object.keys(response.data)[a];
          this[key] = response.data[key];
        }
        await nextTick().then(() => {
          console.log('resetting');
          this.resetQuestion();
          this.loading = false;
          this.readyToRun = true;
        });
      }).catch((err) => {
        console.log(err.message);
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    async deleteBulkData(data) {
      this.loading = true;
      axios.post(`${api}/delete${data.collection}`, { records: data.records }, await auth(this.user))
      .then(() => {
        for (let a = 0; a < data.records.length; a += 1) {
          const i = this[data.collection].findIndex((x) => x._id === data.records[a]);
          if (i > -1) {
            this[data.collection].splice(i, 1);
          }
        }
        this.loading = false;
      })
      .catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    async deleteData(data) {
      this.loading = true;
      console.log(data);
      axios.delete(`${api}/delete${data.collection}/${data.record}`, await auth(this.user))
      .then(() => {
          const i = this[data.collection].findIndex((x) => x._id === data.record);
          if (i > -1) {
            this[data.collection].splice(i, 1);
          }
          this.loading = false;
      })
      .catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    getItem(data) {
      const id = data.record;
      const coll = data.collection;
      return this[coll].find((f) => f._id === id);
    },
    async addQuestionToGame(qId) {
      const q = this.questions.find((f) => f._id === qId);
      const round = this.CurrentGame.activeRound;
      const question = this.CurrentGame.activeQuestion;
      if (!this.CurrentGame.rounds[round]) this.CurrentGame.rounds[round] = [];
      if (!this.CurrentGame.rounds[round][question]) {
        this.CurrentGame.rounds[round].push(q);
      } else {
        this.CurrentGame.rounds[round].splice(question + 1, 0, q);
      }
    },
    async addMultipleQuestionsToGame(qIds) {
      for (let a = 0; a < qIds.length; a += 1) {
        const q = this.questions.find((f) => f._id === qIds[a]);
        if (q) {
          this.CurrentGame.rounds[this.CurrentGame.activeRound].push(q);
        }
      }
    },
    async loadGame(id) {
      this.loading = true;
      axios.get(`${api}/game/${id}`, await auth(this.user))
      .then((response) => {
        this.CurrentGame = { ...response.data, };
        this.CurrentGameRevert = { ...response.data, };
        this.loading = false;
      }).catch((err) => {
        console.log(err)
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    async copyGame(id) {
      this.loading = true;
      axios.get(`${api}/game/${id}`, await auth(this.user))
      .then((response) => {
        const game = { ...response.data, };
        delete game._id;
        for (let a = 0; a < Object.keys(game.rounds).length; a += 1) {
          const key = Object.keys(game.rounds)[a];
          for (let b = 0; b < game.rounds[key].length; b += 1) {
            delete game.rounds[key][b]._id;
          }
        }
        this.CurrentGame = { ...game };
        this.CurrentGameRevert = { ...game };
        this.loading = false;
      }).catch((err) => {
        console.log(err)
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    resetScoreboard() {
      this.CurrentGame.scoreboard = [];
    },
    updateScoreboard(data) {
      console.log('scoreboard updated');
      this.CurrentGame.scoreboard = data;
    },
    unloadGame() {
      this.LastGame = { ...this.CurrentGame };
      // this.CurrentGame = this.LastGame;
      this.CurrentGame = CurrentGameDefaults();
      this.CurrentGameRevert = CurrentGameDefaults();
    },
    async saveQuestion(data, autoAdd) {
      this.loading = true;
      axios.post(`${api}/questions`, { record: data.records }, await auth(this.user))
      .then((response) => {
        if (autoAdd) {
          this.questions.push(response.data.newData);
        } else {
          this.SavedQuestion = response.data.newData;
        }
        this.loading = false;
      }).catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    async saveGame() {
      this.loading = true;
      const records = this.CurrentGame;
      axios.post(`${api}/games`, { records }, await auth(this.user))
      .then((response) => {
        this.CurrentGame = { ...response.data };
        this.games.push({ ...response.data });
        const gameIndex = this.games.findIndex((f) => f._id === this.CurrentGame._id);
        for (let a = 0; a < Object.entries(this.games[gameIndex].rounds).length; a += 1) {
          const round = Object.entries(this.games[gameIndex].rounds)[a][1];
          for (let b = 0; b < round.length; b += 1) {
            const q = round[b];
            const qIndex = this.questions.findIndex((f) => f._id === q._id);
            if (qIndex > -1) {
              this.questions[qIndex] = { ...q };
            } else {
              this.questions.push({ ...q });
            }
          }
        }
        this.loading = false;
        this.status = 'Game saved';
        setTimeout(() => {
          this.status = null;
        }, 4000);
      }).catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.status = 'Error saving game';
        this.loading = false;
      });
      // function onComplete() {
      //   for (let a = 0; a < newRecords.length; a += 1) {
      //     this.questions.push(newRecords[a]);
      //     if (forRound || forRound === 0) {
      //       if (!this.currentGame.rounds[forRound]) this.currentGame.rounds[forRound] = [];
      //       this.currentGame.rounds[forRound].push(JSON.parse(JSON.stringify(newRecords[a])));
      //     }
      //   }
      // }
    },
    async updateGame() { // Update the currently-loaded game
      console.log('updaing the game')
      this.loading = true;
      const record = this.CurrentGame;
      axios.put(`${api}/games/`, { record }, await auth(this.user))
      .then((response) => {
        this.CurrentGame = { ...response.data };
        this.loading = false;
        this.status = 'Game saved';
        // Update DB with saved data
        const gameIndex = this.games.findIndex((f) => f._id === this.CurrentGame._id);
        if (gameIndex > -1) {
          this.games[gameIndex] = { ...response.data };
        }
        for (let a = 0; a < Object.entries(this.games[gameIndex].rounds).length; a += 1) {
          const round = Object.entries(this.games[gameIndex].rounds)[a][1];
          for (let b = 0; b < round.length; b += 1) {
            const q = round[b];
            const qIndex = this.questions.findIndex((f) => f._id === q._id);
            if (qIndex > -1) {
              this.questions[qIndex] = { ...q };
            } else {
              this.questions.push({ ...q });
            }
          }
        }
        console.log('game saved', this.CurrentGame);
        setTimeout(() => {
          this.status = null;
        }, 4000);
      }).catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.status = 'Error saving game';
        this.loading = false;
      });
      // function onComplete() {
      //   for (let a = 0; a < newRecords.length; a += 1) {
      //     this.questions.push(newRecords[a]);
      //     if (forRound || forRound === 0) {
      //       if (!this.currentGame.rounds[forRound]) this.currentGame.rounds[forRound] = [];
      //       this.currentGame.rounds[forRound].push(JSON.parse(JSON.stringify(newRecords[a])));
      //     }
      //   }
      // }
    },
    async saveData(data) {
      this.loading = true;
      axios.post(`${api}/${data.collection}`, { records: data.records }, await auth(this.user))
      .then((response) => {
        for (let a = 0; a < response.data.newData.length; a += 1) {
          this[data.collection].push(response.data.newData[a]);
        }
        this.loading = false;
      }).catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    async updateData(data) {
      this.loading = true;
      axios.put(`${api}/${data.collection}`, { record: data.record }, await auth(this.user))
      .then((res) => {
        console.log('res', res);
        this.loading = false;
      }).catch((err) => {
        console.log(err);
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    // Duplcate existing game by moving all questions to a new game
    async duplicateGame(id) {
      let existingGame;
      this.loading = true;
      axios.get(`${api}/game/${id}`, auth(this.user))
      .then((response) => {
        console.log('this one', response.data);
        existingGame = { ...response.data };
        const newGame = CurrentGameDefaults();
        newGame.rounds = { ...existingGame.rounds };
        this.CurrentGame = { ...newGame, };
        this.CurrentGameRevert = { ...newGame, };
        this.loading = false;
      }).catch((err) => {
        console.log(err)
        this.systemErrorText = err;
        this.loading = false;
      });
    },
    setQuestion(q, fromApi) {
      this.CurrentGame.activeQuestion = q;
      this.CurrentGame.showAnswer = false;
      this.CurrentGame.timer = false;
      this.CurrentGame.questionStep = -1;
      this.CurrentGame.questionSubStep = 0;
      const payload = {
        activeQuestion: this.CurrentGame.activeQuestion,
        questionStep: -1,
        questionSubStep: 0,
        timer: false,
        timeLeft: this.CurrentGame.rounds[this.CurrentGame.activeRound][q].timeAllotted,
        showAnswer: false,
      }
      const message = JSON.stringify(payload);
      if (!fromApi) {
        this.webSocketStore.sendMessage(message);
      }
      this.resetQuestion();
      if (this.preferences.showCategoryIntro) {
        this.showCategoryIntro = true;
        setTimeout(() => {
          this.showCategoryIntro = false;
        }, this.preferences.categoryIntroDuration);
      }
    },
    setRound(r, q, fromApi) {
      this.CurrentGame.activeRound = r;
      this.CurrentGame.activeQuestion = 0;
      this.CurrentGame.interlude.show = !!(this.CurrentGame.interlude.useInterlude);
      this.lastRound = r;
      this.CurrentGame.showAnswer = false;
      this.CurrentGame.timer = false;
      // this.CurrentGame.questionStep = -1;
      // this.CurrentGame.questionSubStep = 0;
      const payload = {
        activeRound: this.CurrentGame.activeRound,
        interlude: this.CurrentGame.interlude,
        activeQuestion: 0,
        questionStep: -1,
        questionSubStep: 0,
        timer: false,
        showAnswer: false,
      };
      if (q || q === 0) {
        this.CurrentGame.activeQuestion = q;
        payload.activeQuestion = this.CurrentGame.activeQuestion;
      }
      try {
        payload.timeLeft = this.CurrentGame.rounds[this.CurrentGame.activeRound][this.CurrentGame.activeQuestion].timeAllotted;
      } catch (e) {
        payload.timeLeft = 0;
      }
      const message = JSON.stringify(payload);
      this.resetQuestion();
      if (!fromApi) {
        this.webSocketStore.sendMessage(message);
      }
    },
    setInterlude(data, fromApi) {
      this.CurrentGame.interlude.show = data;
      this.CurrentGame.showAnswer = false;
      this.CurrentGame.timer = false;
      const payload = {
        showInterlude: this.CurrentGame.interlude.show,
        timer: false,
        showAnswer: false,
      };
      const message = JSON.stringify(payload);
      if (!fromApi) {
        this.webSocketStore.sendMessage(message);
      }
      if (!data) {
        if (this.preferences.showCategoryIntro) {
          this.showCategoryIntro = true;
          setTimeout(() => {
            this.showCategoryIntro = false;
          }, this.preferences.categoryIntroDuration);
        }
      }
    },
    setQuestionStep(step, fromApi) {
      this.CurrentGame.questionStep = step;
      if (!fromApi) {
        const payload = {
          questionStep: step,
        };
        const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      }
    },
    setQuestionSubStep(step, fromApi) {
      this.CurrentGame.questionSubStep = step;
      if (!fromApi) {
        const payload = {
          questionSubStep: step,
        };
        const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      }
    },
    setTimer(data, fromApi) {
      const payload = {};
      if (data.timer !== undefined) {
        this.CurrentGame.timer = data.timer;
        payload.timer = this.CurrentGame.timer;
      }
      if (data.timeLeft || data.timeLeft === 0) {
        this.CurrentGame.timeLeft = data.timeLeft;
        payload.timeLeft = this.CurrentGame.timeLeft;
      }
      if (!this.CurrentGame.timer || timerIntervals.length > 0) {
        timerIntervals.forEach((interval) => {
          clearInterval(interval);
        });
        timerIntervals = [];
      }
      if (!fromApi) {
        const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      }
      if (this.CurrentGame.timer) {
        const timerInterval = setInterval(() => {
          if (this.CurrentGame.timer && this.CurrentGame.timeLeft > 0) {
            this.CurrentGame.timeLeft -= 1;
          } else if (!this.CurrentGame.timer) {
            clearInterval(timerInterval);
            timerIntervals = [];
          }
        }, 1000);
        timerIntervals.push(timerInterval);
      }
    },
    setAnswer(data, fromApi) {
      const payload = {
        setAnswer: data,
      };
      this.CurrentGame.showAnswer = data;
      if (!fromApi) {
        const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      }
    },
    setTeam(data, fromApi) {
      const teamIndex = this.CurrentGame.scoreboard.findIndex((f) => f._id === data._id);
      if (teamIndex > -1) {
        this.CurrentGame.scoreboard[teamIndex] = data;
      } else {
        this.CurrentGame.scoreboard.push(data);
      }
      if (!fromApi) {
        const payload = { team: data };
        const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      }
    },
    showScoreboard(fromApi) {
      this.CurrentGame.showScoreboard = !this.CurrentGame.showScoreboard;
      if (!fromApi) {
        const payload = {
          showScoreboard: !this.CurrentGame.showScoreboard,
        };
      const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      }
    },
    addTeam(teamName, fromApi) {
      const team = {
        _id: useId(),
        name: teamName || '',
        score: 0,
        toAdd: 0,
        order: this.CurrentGame.scoreboard.length + 1,
        ranking: {
          tie: false,
          rank: this.CurrentGame.scoreboard.length + 1,
        },
        color: FormatStyles[Math.floor(Math.random() * FormatStyles.length)],
      };
      this.CurrentGame.scoreboard.push(team);
      const payload = {
        team,
      };
      const message = JSON.stringify(payload);
      if (!fromApi) {
        this.webSocketStore.sendMessage(message);
      }
    },
    advanceQuestion(fromApi) {
      const currentQuestion = this.CurrentGame.rounds[this.CurrentGame.activeRound][this.CurrentGame.activeQuestion];
      let nextSeg = null;
      let toDisplay = null;

      for (let i = 0; i < currentQuestion.segments.length; i += 1) {
        const segment = currentQuestion.segments[i];
        nextSeg = currentQuestion.segments[i + 1];

        // Check if the current segment is already displayed and handle clueAnswer segment
        if ((nextSeg && !nextSeg.meta.display) || !nextSeg) {
          if (segment.type === 'clueAnswer') {
            segment.meta.display = true;
            // Ensure the subsegment display logic works as expected
            if (segment.meta.displaySubsegment < segment.data.length) {
              // Display the current subsegment (not the next one prematurely)
              const subsegmentIndex = segment.meta.displaySubsegment;
              this.upcomingSegment = segment.data[subsegmentIndex]; // Show current subsegment
              segment.meta.displaySubsegment += 1; // Move to the next subsegment for the next press
              
              // Return early to display the subsegment one by one
              return;
            }
          }
        }

        // Find the next undisplayed segment
        if (!segment.meta.display) {
          toDisplay = i;
          break;
        }
      }

      // Mark the segment as displayed
      if (toDisplay !== null) {
        currentQuestion.segments[toDisplay].meta.display = true;
        this.upcomingSegment = nextSeg && nextSeg.data ? nextSeg.data : null;
        if (!fromApi) {
          this.webSocketStore.sendMessage(JSON.stringify({ questionStep: true }));
        }
      } else {
        this.setTimer({ timer: !this.CurrentGame.timer });
      }
    },
    resetQuestion() {
      if (this.CurrentGame.rounds[this.CurrentGame.activeRound]) {
        this.CurrentGame.showAnswer = false;
        this.CurrentGame.timer = false;
        let q = this.CurrentGame.rounds[this.CurrentGame.activeRound][this.CurrentGame.activeQuestion];
        this.CurrentGame.timeLeft = this.CurrentGame.rounds[this.CurrentGame.activeRound][this.CurrentGame.activeQuestion].timeAllotted;
        if (q && q.segments) {
          for (let i = 0; i < q.segments.length; i += 1) {
            q.segments[i].meta.display = false;
            q.segments[i].meta.displaySubsegment = -1;
          }
          this.upcomingSegment = q.segments[0];
        }
      }
    },
    async resetQuestions() {
      // Reset meta.display on all questions
      console.log('resetting questions');
      for (let a = 0; a <this.CurrentGame.rounds.length; a += 1) {
        for (let i = 0; i < this.CurrentGame.rounds[a].length; i += 1) {
          const q = this.CurrentGame.rounds[a][i];
          if (q && q.segments) {
            for (let j = 0; j < q.segments.length; j += 1) {
              q.segments[j].meta.display = false;
              q.segments[j].meta.displaySubsegment = 0;
            }
          }
        }
      }
      console.log('questions reset', new Date());
      return;
    },
    setScores(scoreboard, fromApi) {
      if (!fromApi) {
        const payload = {
          scoreboard: this.CurrentGame.scoreboard,
        };
        const message = JSON.stringify(payload);
        this.webSocketStore.sendMessage(message);
      } else {
        this.CurrentGame.scoreboard = scoreboard;
      }
      const teams = this.CurrentGame.scoreboard;
      if (this.preferences && this.preferences.animateScores) {
        for (let i = teams.length - 1; i >= 0; i--) {
          const team = teams[i];
          const interval = setInterval(() => {
            if (team.toAdd > 0) {
              team.score += 1;
              team.toAdd -= 1;
            } else if (team.toAdd < 0) {
              team.score -= 1;
              team.toAdd += 1;
            } else {
              clearInterval(interval);
              // Sort teams based on score
              teams.sort((a, b) => b.score - a.score);
              // Update ranking
              teams.forEach((team, index) => {
                team.ranking.rank = index + 1;
              });
            }
          }, 100); // Adjust the interval time as needed
        }
      } else {
        teams.forEach(team => {
          team.score += team.toAdd;
          team.toAdd = 0;
        });
        // Sort teams based on score
        teams.sort((a, b) => b.score - a.score);
        // Update ranking
        teams.forEach((team, index) => {
          team.ranking.rank = index + 1;
        });
      }
    }
  }
})
